import { BiButton, BiButtonGroup, BiNotification } from 'bi-web-components-react';
import { useState } from 'react';

import { Programme } from '../../types';
import StudyPlanner from '../programmePage/studyPlanner';

import { useTranslation } from 'react-i18next';
import { addToCart } from '../../api';
import { useCartContext } from '../../context/CartContext';
import useRedirectLoading from '../../hooks/useRedirectLoading';
import {
    addVoiceTextElementToCart,
    getTermTranslationKey,
    hasProperty,
    isArrangementInCart,
    mapCampusCodeToCampus,
    openCart,
} from '../../utils';
import ErrorDialog from '../errorDialog/ErrorDialog';
import { ListItem } from './listItem/ListItem';
import useSWRImmutable from 'swr/immutable';
import styles from './applyNow.module.scss';
import { Pending } from '@bino/utils/components/pending';
import {
    BiIconCalendar,
    BiIconCreditCard,
    BiIconLocation,
    BiIconWorld,
    BiIconClock,
    BiIconSchool,
    BiIconPrice,
} from '@bi-digital/icons-react';
import { trackGAEventForApplication } from '@bino/utils/functions';

const getPathForProgram = (programmeId?: number | null) => {
    if (!programmeId) return null;
    if (!window.location.pathname.endsWith('/')) {
        const pathPrefix = location.pathname.split('/').at(-1);
        return `${pathPrefix}/GetProductInfo?programmeId=${programmeId}`;
    }
    return `GetProductInfo?programmeId=${programmeId}`;
};

export default function ApplyNow() {
    const urlParams = new URLSearchParams(window.location.search);
    const arrangementBannerId = urlParams.get('a');
    const { isAuthorized, programmeId, cart, refreshCart, disableCart, emergencyContactFormUrl } = useCartContext();

    const { data: programme, isLoading: isLoadingProgram } = useSWRImmutable<Programme>(getPathForProgram(programmeId));

    const {
        hideApplyButton,
        campuses,
        credits,
        price,
        whenCanIApply,
        arrangements,
        courseLoad,
        teachingLanguage,
        name,
        isCourse,
    } = programme || {};

    const { t } = useTranslation();
    const { redirectLoading, startRedirectLoading } = useRedirectLoading();
    const [submitting, setSubmitting] = useState(false);
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const [selector, setSelector] = useState<'applynow' | 'planner'>(
        arrangementBannerId === null ? 'applynow' : 'planner',
    );

    const isOneYearProgramme = name?.includes('studieår') || false; // Major hack and very fragile

    const startIsNotFirstYear =
        !isOneYearProgramme &&
        arrangements?.length === 1 &&
        arrangements[0].studentType !== '0' && // executive studies
        arrangements[0].studentType !== '1'; // first year

    function handleCartError(message: string) {
        setErrorMessage(message);
        setErrorDialogOpen(true);
        setSubmitting(false);
    }
    const addArrangemnetToCart = async () => {
        setSubmitting(true);
        if (arrangements) {
            const isItemInCart = isArrangementInCart(arrangements[0].bannerId, cart.items);

            if (isItemInCart) {
                return handleCartError(t('cart:errorAlreadyExists'));
            }

            try {
                trackGAEventForApplication('addToCart', [
                    {
                        id: arrangements[0].productId,
                        name: programme?.name || 'Not Available',
                        campus: mapCampusCodeToCampus[arrangements[0].campusCode],
                    },
                ]);

                await addToCart(arrangements, isAuthorized);
                if (isAuthorized) {
                    await refreshCart();
                    openCart();
                    addVoiceTextElementToCart(t('cart:addedtocart'));
                    setSubmitting(false);
                } else {
                    startRedirectLoading();
                }
            } catch (error) {
                const errorMsg =
                    hasProperty(error, 'type') && error.type === 'ItemAlreadyAdded'
                        ? t('cart:errorAlreadyExists')
                        : t('cart:errorPostingToCart');
                handleCartError(errorMsg);
            }
        }
    };

    const programmeIdIsEmpty = programmeId === 0 || !programmeId;
    const urlSchedule = window.location.href.includes('#practicalInfoSection')
        ? window.location.href
        : window.location.href + programme?.urlSchedule;

    const creditsText = credits ? `${credits} ${t('cart:credits')}` : null;
    const countArrangements = arrangements?.length || 0;
    const teachingLanguageText = teachingLanguage === 'Norwegian' ? t('cart:nolanguage') : t('cart:enlanguage');
    const urlPrice = '#' + t('cart:tuitionFeeSection');

    const startTerms = [
        ...new Set(
            arrangements?.map(({ termCode }) => {
                const [term, year] = getTermTranslationKey(termCode);
                return `${t(term)} ${year}`;
            }),
        ),
    ].join(', ');

    async function apply() {
        if (countArrangements === 1) {
            return await addArrangemnetToCart();
        } else {
            if (arrangements) {
                trackGAEventForApplication('selectArrangement', [
                    {
                        id: arrangements[0].productId,
                        name: name ?? 'Not Available',
                        campus: '',
                    },
                ]);
            }
            setSelector('planner');
        }
    }

    if (!programme && isLoadingProgram) return <Pending />;
    if (!programme) return null;

    if (selector === 'applynow') {
        return (
            <div className={styles.container}>
                <ul className={styles.chipList}>
                    <ListItem
                        icon={BiIconLocation}
                        text={campuses?.join(', ')}
                        srOnlyText={t('cart:LocationSrOnly') + campuses?.join(', ')}
                    />
                    <ListItem
                        icon={BiIconClock}
                        text={courseLoad}
                        srOnlyText={t('cart:StudyLoadSrOnly') + courseLoad}
                    />
                    <ListItem
                        icon={BiIconCalendar}
                        text={startTerms}
                        srOnlyText={t('cart:StartDateSrOnly') + startTerms}
                    />
                    <ListItem
                        icon={BiIconSchool}
                        text={creditsText}
                        srOnlyText={t('cart:CreditsSrOnly') + creditsText}
                    />
                    <ListItem
                        icon={BiIconWorld}
                        text={teachingLanguageText}
                        srOnlyText={t('cart:LanguageSrOnly') + teachingLanguageText}
                    />

                    {!programme?.hasPriceAndTuitionFeeBlock && isCourse && price && (
                        <ListItem icon={BiIconPrice} text={price?.toString()} />
                    )}

                    {programme?.hasPriceAndTuitionFeeBlock && (
                        <ListItem
                            icon={BiIconCreditCard}
                            text={t('cart:tuitionFee')}
                            link={urlPrice}
                            srOnlyText={t('cart:tuitionFee')}
                            type='link'
                        />
                    )}

                    {programme?.urlSchedule && (
                        <ListItem
                            icon={BiIconCalendar}
                            text={t('cart:schedule')}
                            srOnlyText={t('cart:ScheduleSrOnly') + t('cart:schedule')}
                            link={urlSchedule}
                            type='link'
                        />
                    )}
                </ul>

                {startIsNotFirstYear && (
                    <div>
                        <BiNotification
                            hideCloseButton
                            color='yellow'
                            heading={t('cart:importantInformation')}
                            icon='warning'
                            message={
                                arrangements && arrangements[0].studentType === '2'
                                    ? t('cart:startOpenOnlySecondYear')
                                    : t('cart:startOpenOnlyThirdYear')
                            }
                        />
                    </div>
                )}

                {disableCart && emergencyContactFormUrl ? (
                    <BiButtonGroup alignment='center'>
                        <BiButton
                            className={styles.applyButton}
                            stretch
                            submitting={redirectLoading || submitting}
                            disabled={redirectLoading || submitting}
                            href={emergencyContactFormUrl}
                        >
                            {t('cart:applynow')}
                        </BiButton>
                    </BiButtonGroup>
                ) : (
                    !hideApplyButton && (
                        <BiButtonGroup alignment='center'>
                            <BiButton
                                className={styles.applyButton}
                                stretch
                                submitting={redirectLoading || submitting}
                                disabled={redirectLoading || submitting}
                                onClick={() => apply()}
                            >
                                {t('cart:applynow')}
                            </BiButton>
                        </BiButtonGroup>
                    )
                )}

                {hideApplyButton && whenCanIApply && !programmeIdIsEmpty && (
                    <BiButtonGroup alignment='center'>
                        <span className={styles.applyButton}>
                            <BiButton stretch name='apply-btn' href={programme?.whenCanIApplyUrl}>
                                {t('cart:whencaniapply')}
                            </BiButton>
                        </span>
                    </BiButtonGroup>
                )}

                {errorDialogOpen && errorMessage && (
                    <ErrorDialog
                        message={errorMessage}
                        heading={t('cart:infoHeading')}
                        icon='information'
                        color='blue'
                        onClose={() => setErrorDialogOpen(false)}
                    />
                )}
            </div>
        );
    }

    if (selector === 'planner') {
        return <StudyPlanner programme={programme!} />;
    }
}
